import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type {
  SystemSsoConfig,
  SystemSsoConfigPayload,
  SystemSsoPortalAssignmentsResponse,
  SystemSsoPortalAssignmentUpdate,
} from "@/types";

const SSO_CONFIG_KEY = ["system-sso", "config"] as const;
const SSO_ASSIGNMENTS_KEY = ["system-sso", "portal-assignments"] as const;

export const useSystemSsoConfig = () => {
  return useQuery({
    queryKey: SSO_CONFIG_KEY,
    queryFn: async (): Promise<SystemSsoConfig | null> => {
      const { data } = await apiClient.get(API_ENDPOINTS.SYSTEM.SSO);
      return data;
    },
    staleTime: 60 * 1000,
  });
};

export const useUpdateSystemSsoConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SystemSsoConfigPayload) => {
      const { data } = await apiClient.put(API_ENDPOINTS.SYSTEM.SSO, payload);
      return data as SystemSsoConfig;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(SSO_CONFIG_KEY, data);
    },
  });
};

export const useSystemSsoPortalAssignments = () => {
  return useQuery({
    queryKey: SSO_ASSIGNMENTS_KEY,
    queryFn: async (): Promise<SystemSsoPortalAssignmentsResponse> => {
      const { data } = await apiClient.get(
        API_ENDPOINTS.SYSTEM.SSO_PORTAL_ASSIGNMENTS
      );
      return data;
    },
    staleTime: 60 * 1000,
  });
};

export const useUpdateSystemSsoPortalAssignments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assignments: SystemSsoPortalAssignmentUpdate[]) => {
      const { data } = await apiClient.patch(
        API_ENDPOINTS.SYSTEM.SSO_PORTAL_ASSIGNMENTS,
        { assignments }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SSO_ASSIGNMENTS_KEY });
    },
  });
};
